import { Component, ViewChild } from '@angular/core';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CardComponent } from '@shared/components/card/card.component';
import { BusinessRole, PagedResultOfPartySearchResult, PartiesService, PartySearchResult } from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { TableComponent } from '@shared/components/table/table.component';
import { HeaderService } from '@app/core/services/header.service';
import { actorRegisterListMenu } from '@views/actorregister/actorregister-list/actorregister.menu-action';
import { ModalService } from '@app/core/services/modal.service';
import {
  CreateActorModalComponent
} from '@shared/modals/actorregister/create-actor-modal/create-actor-modal.component';
import { EditActorModalComponent } from '@shared/modals/actorregister/edit-actor-modal/edit-actor-modal.component';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { actorTableConfig } from '@shared/table-configs/actor/actor.table';
import { Observable } from 'rxjs';
import { sortByTranslation } from '@app/core/utils/sort-by-translation';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { SortParam } from '@app/core/constants/sort-param';

@Component({
  selector: 'app-actorregister-list',
  imports: [
    HeaderComponent,
    TranslatePipe,
    CardComponent,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    InputDirective,
    ButtonDirective,
    TableComponent,
    SelectDirective
  ],
  templateUrl: './actorregister-list.component.html',
  styleUrl: './actorregister-list.component.css'
})
export class ActorregisterListComponent {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  /** Form **/
  public form: FormGroup = new FormGroup({
    businessRole: new FormControl(),
    name: new FormControl(),
    identification: new FormControl<string | null>(null),
  });

  /** Actions **/
  public actions: MenuAction[] = [
    {
      ...actorRegisterListMenu[0],
      callback: () => this.openCreateModal(),
    },
    {
      ...actorRegisterListMenu[1],
      callback: () => this.openEditModal(),
      disabled: () => this.selectedElements.length !== 1,
    }
  ]

  /** Table **/
  public actorTableConfig: TableConfig<PartySearchResult> = {
    ... actorTableConfig,
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.searchHandler(pageSize, page, sort),
    contextActions: [this.actions[1]]
  }
  public actorTableData: PartySearchResult[] = [];
  public selectedElements: PartySearchResult[] = [];

  /** Business role **/
  public businessRoleList: KeyValue[] = sortByTranslation(enumToKeyValue(BusinessRole, { omitKeys: ['NotSpecified'], prefixValue: 'actor.businessrole.' }));

  constructor(
    private readonly partiesService: PartiesService,
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
  ) {
    /** Set Menu **/
    this.headerMenuService.setActions(this.actions)

    /** Search out of the gate **/
    this.searchParty()
  }

  public searchHandler(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    const query = removeNullValues(this.form.value);
    return this.partiesService.partiesSearchPost(query, page, pageSize);
  }

  /**
   * Search for a party
   */
  public searchParty(): void {
    if (this.form.valid) {
      const query = removeNullValues(this.form.value);
      this.partiesService.partiesSearchPost(query, 1, 20).subscribe((result: PagedResultOfPartySearchResult) => {
        this.actorTableData = result.results ?? [];
      });
    }
  }

  public openCreateModal(): void {
    this.modalService.show({
      component: CreateActorModalComponent,
      title: 'actorregister.create.title',
      actions: [
        { type: 'primary', label: 'system.button.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
    }).subscribe(() => {
      this.table?.search()
    });
  }

  public openEditModal(): void {
    this.modalService.show({
      component: EditActorModalComponent,
      title: 'actorregister.edit.title',
      actions: [
        { type: 'primary', label: 'system.button.edit', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        id: this.selectedElements[0].id,
      }
    }).subscribe(() => {
      this.table?.search()
    });
  }
}
